<template>
  <v-container fluid>
    <v-card>
      <v-card-title class="text-h6">
        Order Distribution By Meal Space Used (aka Required Boxes)
        <v-spacer/>
        <ShippingGroupSelector
          :dates="selectedDates"
          :shipping-group="selectedShippingGroup"
          @update="handleProductionUpdate"
        />
        <v-spacer/>
        <v-btn-toggle v-model="chartType" mandatory>
          <v-btn value="bar">Bar</v-btn>
          <v-btn value="table">Table</v-btn>
        </v-btn-toggle>
      </v-card-title>
      <v-card-text>
        <v-tabs v-model="activeTab">
          <v-tab>By Rack</v-tab>
          <v-tab>By Box Size</v-tab>
          <v-tab>Raw</v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab">
          <v-tab-item>
            <!-- By rack configuration: one bar per rack, grouped by box size,
                 with an unconstrained "size total" bar leading each group -->
            <v-row align="center" class="px-4">
              <v-col cols="12" md="9" class="caption grey--text text--darken-1 pt-2">
                Racks grouped by box size. Each group starts with an unconstrained total
                (all racks of that size). Blue = covered demand, green stripes = spare
                capacity, red = demand over capacity. Chips show restrictions.
              </v-col>
              <v-col cols="12" md="3" class="d-flex justify-end">
                <v-switch
                  v-model="showSplit"
                  label="Show split demand"
                  dense hide-details class="mt-0"
                />
              </v-col>
            </v-row>
            <v-row v-if="chartType === 'bar'">
              <v-col cols="12">
                <div class="chart-wrapper">
                  <div class="y-axis-label">Orders (box demand)</div>
                  <div class="chart-container rack-chart">
                    <div class="y-axis">
                      <div class="y-tick" v-for="tick in 6" :key="'y-tick-' + tick"
                           :style="{ bottom: ((tick - 1) * 20) + '%' }">
                        {{ Math.round((maxGroupedValue / 5) * (tick - 1)) }}
                      </div>
                    </div>
                    <div class="bars-container">
                      <div
                        v-for="(b, index) in rackBars"
                        :key="b.key"
                        :class="[barContainerClass, {'size-group-start': b.isTotal}]"
                      >
                        <div class="col-plot">
                          <div class="cap-top">
                            <span v-if="b.isTotal">{{ b.usableCapacity }} boxes</span>
                            <span v-else-if="b.usableCapacity > 0">{{ b.rackCount }} racks · {{ b.usableCapacity }} boxes</span>
                            <span v-else class="grey--text">no usable racks</span>
                          </div>
                          <div v-if="b.overflowKind === 'split'" class="over-dest split-out">
                            → split to {{ b.splitTargets.join(' + ') }}
                          </div>
                          <div v-else-if="b.overflowKind === 'regular'" class="over-dest">
                            → regular racks
                          </div>
                          <div class="bar-container-wrapper" style="position: relative;">
                            <!-- blue = covered demand, green stripes = spare capacity,
                                 red = demand over capacity; height = max(demand, capacity) -->
                            <div
                              class="rack-stack"
                              :class="{'is-total': b.isTotal}"
                              :style="{ height: (b.barTotal / maxGroupedValue * 240) + 'px' }"
                            >
                              <div
                                v-if="b.spareCap > 0"
                                class="rack-seg spare-seg"
                                :style="{ height: segmentPct(b.spareCap, b.barTotal) + '%' }"
                              >
                                <span class="seg-num spare-num">{{ b.spareCap }}</span>
                              </div>
                              <div
                                v-if="b.overCap > 0"
                                class="rack-seg over-seg"
                                :style="{ height: segmentPct(b.overCap, b.barTotal) + '%' }"
                              >
                                <span class="seg-num">{{ b.overCap }}</span>
                              </div>
                              <div
                                v-if="b.coveredDemand > 0"
                                class="rack-seg covered-seg"
                                :style="{ height: segmentPct(b.coveredDemand, b.barTotal) + '%' }"
                              >
                                <span class="seg-num">{{ b.coveredDemand }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="col-label rack-col-label">
                          <div class="demand-label">need {{ b.totalDemand }}</div>
                          <div class="box-type-label" :class="{'total-label': b.isTotal}">{{ b.boxType }}</div>
                          <div class="range-label">holds {{ b.end }} ({{ b.start }}–{{ b.end }})</div>
                          <div class="chip-row">
                            <span
                              v-for="(chip, ci) in b.chips" :key="'chip-' + index + '-' + ci"
                              class="mini-chip" :class="'mini-chip-' + chip.color">{{ chip.text }}</span>
                          </div>
                          <div class="split-in-below">{{ b.splitInCount > 0 ? '+' + b.splitInCount + ' split-in' : '' }}</div>
                        </div>
                      </div>
                      <!-- Unboxable orders (no matching box size) -->
                      <div v-if="ungroupedData.count > 0" :class="[barContainerClass, 'size-group-start']">
                        <div class="col-plot">
                          <div class="cap-top">&nbsp;</div>
                          <div class="bar-container-wrapper" style="position: relative;">
                            <div
                              class="rack-stack"
                              :style="{ height: (ungroupedData.count / maxGroupedValue * 240) + 'px' }"
                            >
                              <div class="rack-seg noboxsize-seg" style="height: 100%;">
                                <span class="seg-num">{{ ungroupedData.count }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="col-label rack-col-label">
                          <div class="demand-label">need {{ ungroupedData.count }}</div>
                          <div class="box-type-label ungrouped">No box size</div>
                          <div class="range-label">{{ ungroupedData.rangeLabel }} meals</div>
                          <div class="chip-row"></div>
                          <div class="split-in-below"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="x-axis-label">Rack configuration (grouped by box size)</div>
                  <div class="chart-legend">
                    <div class="legend-item">
                      <div class="legend-color-box" style="background-color: #1976D2;"></div>
                      <span>Covered demand</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color-box spare-swatch"></div>
                      <span>Spare capacity</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color-box" style="background-color: #E53935;"></div>
                      <span>Demand over capacity</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color-box" style="border: 2px solid #37474F; background: transparent;"></div>
                      <span>Size total (unconstrained)</span>
                    </div>
                    <div v-if="ungroupedData.count > 0" class="legend-item">
                      <div class="legend-color-box" style="background-color: #FFA726;"></div>
                      <span>No matching box size</span>
                    </div>
                    <div v-if="hasSplitActivity" class="legend-item">
                      <div class="legend-color-box" style="background-color: #9C27B0;"></div>
                      <span>Split in / out (box overflow)</span>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col cols="12">
                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                    <tr>
                      <th>Rack</th>
                      <th>Box size (meals)</th>
                      <th>Restrictions</th>
                      <th>Demand</th>
                      <th>% of demand</th>
                      <th>Capacity</th>
                      <th>Overflow</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr
                      v-for="(b, index) in rackBars"
                      :key="'group-table-' + b.key"
                      :class="{'size-total-row': b.isTotal}"
                    >
                      <td>{{ b.boxType }}</td>
                      <td>{{ b.end }} <span class="grey--text">({{ b.start }}–{{ b.end }})</span></td>
                      <td>
                        <template v-if="b.isTotal"><span class="grey--text">all racks of this size</span></template>
                        <template v-else>
                          <span
                            v-for="(chip, ci) in b.chips" :key="'tchip-' + index + '-' + ci"
                            class="mini-chip" :class="'mini-chip-' + chip.color">{{ chip.text }}</span>
                          <span v-if="!b.chips.length" class="grey--text">—</span>
                        </template>
                      </td>
                      <td>
                        {{ b.totalDemand }}
                        <span v-if="b.splitInCount > 0" class="split-in-note">(+{{ b.splitInCount }} split-in)</span>
                      </td>
                      <td>{{ totalGroupedCount ? (((b.isTotal ? b.totalDemand : b.demand) / totalGroupedCount) * 100).toFixed(1) : '0.0' }}%</td>
                      <td>
                        {{ b.usableCapacity }} boxes
                        <span v-if="!b.isTotal && b.reservedAway" class="grey--text">(of {{ b.capacity }})</span>
                        <span v-if="b.isTotal" class="grey--text">total</span>
                      </td>
                      <td>
                        <span v-if="b.overflowKind === 'split'">{{ b.splitOutCount }} → split to {{ b.splitTargets.join(' + ') }}</span>
                        <span v-else-if="b.overflowKind === 'regular'">{{ b.overLimit }} → regular racks</span>
                        <span v-else>—</span>
                      </td>
                    </tr>
                    <tr v-if="ungroupedData.count > 0" class="ungrouped-row">
                      <td>No box size</td>
                      <td>{{ ungroupedData.rangeLabel }}</td>
                      <td class="grey--text">—</td>
                      <td>{{ ungroupedData.count }}</td>
                      <td>{{ totalGroupedCount ? ((ungroupedData.count / totalGroupedCount) * 100).toFixed(1) : '0.0' }}%</td>
                      <td class="grey--text">—</td>
                      <td>{{ ungroupedData.count }} → regular racks</td>
                    </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>
            </v-row>
          </v-tab-item>

          <v-tab-item>
            <!-- By box size: demand bucketed by the box size an order needs -->
            <v-row>
              <v-col cols="12" class="text-center caption grey--text text--darken-1 pt-2">
                Orders grouped by the box size they need (the max meals each box holds),
                regardless of which rack or location serves them.
              </v-col>
            </v-row>
            <v-row v-if="chartType === 'bar'">
              <v-col cols="12">
                <div class="chart-wrapper">
                  <div class="y-axis-label">Orders (box demand)</div>
                  <div class="chart-container">
                    <div class="y-axis">
                      <div class="y-tick" v-for="tick in 6" :key="'y-tick-size-' + tick"
                           :style="{ bottom: ((tick - 1) * 20) + '%' }">
                        {{ Math.round((maxBoxSizeValue / 5) * (tick - 1)) }}
                      </div>
                    </div>
                    <div class="bars-container">
                      <div
                        v-for="(b, index) in boxSizeData"
                        :key="'size-' + index"
                        :class="barContainerClass"
                      >
                        <div class="col-plot">
                          <div class="bar-total-label">
                            total {{ b.demand }}<span v-if="b.overLimit > 0" class="over-cap"> · {{ b.overLimit }} over</span>
                          </div>
                          <div class="bar-container-wrapper" style="position: relative;">
                            <!-- stacked: home at the bottom, office on top -->
                            <div class="bar-stack" :style="{ height: (b.demand / maxBoxSizeValue * 300) + 'px' }">
                              <div
                                v-if="b.office > 0"
                                class="bar-seg office-seg"
                                :style="{ height: segmentPct(b.office, b.demand) + '%' }"
                              >
                                <span class="seg-label">office: {{ b.office }}</span>
                              </div>
                              <div
                                v-if="b.home > 0"
                                class="bar-seg home-seg"
                                :style="{ height: segmentPct(b.home, b.demand) + '%' }"
                              >
                                <span class="seg-label">home: {{ b.home }}</span>
                              </div>
                            </div>
                            <div
                              v-if="b.capacity > 0"
                              class="box-count-line-wrapper"
                              :style="{ bottom: Math.min((b.capacity / maxBoxSizeValue) * 300, 300) + 'px' }"
                            >
                              <div class="box-count-label-top">{{ b.capacity }} boxes</div>
                              <div class="box-count-line" :title="`Capacity: ${b.capacity}`"></div>
                            </div>
                          </div>
                        </div>
                        <div class="col-label">
                          <div class="box-type-label">{{ b.size }} meals</div>
                          <div class="range-label">{{ b.min }}–{{ b.size }}</div>
                          <div class="values-label" :title="b.boxTypes.join(', ')">{{ b.boxTypes.join(', ') }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="x-axis-label">Box size (max meals per box)</div>
                  <div class="chart-legend">
                    <div class="legend-item">
                      <div class="legend-color-box" style="background-color: #1976D2;"></div>
                      <span>Home orders</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color-box" style="background-color: #90CAF9;"></div>
                      <span>Office orders</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color-box" style="background-color: #4CAF50; height: 2px; border: 1px dashed red;"></div>
                      <span>Usable capacity (all racks of this size)</span>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col cols="12">
                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                    <tr>
                      <th>Box size (meals)</th>
                      <th>Meal-space range</th>
                      <th>Home</th>
                      <th>Office</th>
                      <th>Total demand</th>
                      <th>% of demand</th>
                      <th>Capacity (usable)</th>
                      <th>Over capacity</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(b, index) in boxSizeData" :key="'size-table-' + index">
                      <td>{{ b.size }}</td>
                      <td>{{ b.min }}–{{ b.size }}</td>
                      <td>{{ b.home }}</td>
                      <td>{{ b.office }}</td>
                      <td>{{ b.demand }}</td>
                      <td>{{ totalBoxSizeDemand ? ((b.demand / totalBoxSizeDemand) * 100).toFixed(1) : '0.0' }}%</td>
                      <td>{{ b.capacity }} boxes</td>
                      <td>{{ b.overLimit > 0 ? b.overLimit : '—' }}</td>
                    </tr>
                    <tr class="font-weight-bold">
                      <td colspan="2">Total</td>
                      <td>{{ boxSizeData.reduce((s, b) => s + b.home, 0) }}</td>
                      <td>{{ boxSizeData.reduce((s, b) => s + b.office, 0) }}</td>
                      <td>{{ totalBoxSizeDemand }}</td>
                      <td>100%</td>
                      <td></td>
                      <td></td>
                    </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>
            </v-row>
          </v-tab-item>

          <v-tab-item>
            <!-- Raw histogram by meal-space, with home / office include toggles -->
            <v-row class="px-4">
              <v-col cols="12" class="d-flex align-center flex-wrap">
                <span class="caption grey--text mr-4">Include:</span>
                <v-checkbox
                  v-model="includeHome" label="Home" dense hide-details class="mr-4 mt-0"
                />
                <v-checkbox
                  v-model="includeOffice" label="Office" dense hide-details class="mt-0"
                />
              </v-col>
            </v-row>
            <v-row v-if="chartType === 'bar'">
              <v-col cols="12">
                <div class="chart-wrapper">
                  <div class="y-axis-label">Number of Orders</div>
                  <div class="chart-container">
                    <div class="y-axis">
                      <div class="y-tick" v-for="tick in 6" :key="'y-tick-raw-' + tick"
                           :style="{ bottom: ((tick - 1) * 20) + '%' }">
                        {{ Math.round((maxRawValue / 5) * (tick - 1)) }}
                      </div>
                    </div>
                    <div class="bars-container">
                      <div
                        v-for="item in rawDistribution"
                        :key="'raw-' + item.space"
                        :class="barContainerClass"
                      >
                        <div class="col-plot">
                          <div class="bar-container-wrapper" style="position: relative;">
                            <div
                              class="bar primary"
                              :style="{ height: (item.count / maxRawValue * 300) + 'px' }"
                            >
                              <div class="bar-value">{{ item.count }}</div>
                            </div>
                          </div>
                        </div>
                        <div class="col-label">
                          <div class="bar-label" :title="item.space">{{ item.space }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="x-axis-label">Meal space (meals per customer)</div>
                </div>
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col cols="12">
                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                    <tr>
                      <th>Meal space</th>
                      <th>Count</th>
                      <th>Percentage</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="item in rawDistribution" :key="'raw-table-' + item.space">
                      <td>{{ item.space }}</td>
                      <td>{{ item.count }}</td>
                      <td>{{ totalRaw ? ((item.count / totalRaw) * 100).toFixed(1) : '0.0' }}%</td>
                    </tr>
                    <tr class="font-weight-bold">
                      <td>Total</td>
                      <td>{{ totalRaw }}</td>
                      <td>100%</td>
                    </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import api from '../api';
import ShippingGroupSelector from '../components/ShippingGroupSelector.vue';
import moment from 'moment';
import urlState from '@/router/urlState';

export default {
  name: 'BinDistribution',
  mixins: [urlState],
  components: {
    ShippingGroupSelector
  },
  data() {
    const today = moment().format('YYYY-MM-DD');
    return {
      selectedDates: [today],
      selectedShippingGroup: null,
      loading: false,
      distributionData: {
        distribution: {},
        distributionAll: {}
      },
      activeTab: 0,
      chartType: 'bar',
      groupedData: [],
      boxSizeData: [],
      includeHome: true,   // Raw tab: include home orders
      includeOffice: true, // Raw tab: include office orders
      showSplit: true,     // By Rack: apply split rules (off = demand before splitting)
      ungroupedData: { count: 0, spaces: [], spacesFormatted: '', rangeLabel: '' },
      windowWidth: window.innerWidth,
      rackConfigs: [], // For storing rack configurations
      splitRules: [] // Box splitting rules (source box -> two target boxes)
    };
  },
  async mounted() {
    this.syncToUrl({
      param: 'selectedDates', urlParam: 'dates', initFromRoute: true,
      parseCallback: (v) => Array.isArray(v) ? v : [v]
    });

    this.syncToUrl({
      param: 'selectedShippingGroup',
      urlParam: 'shippinggroup',
      initFromRoute: true
    });

    this.syncToUrl({
      param: 'chartType',
      initFromRoute: true
    });

    this.syncToUrl({
      param: 'activeTab',
      initFromRoute: true
    });

    // Add window resize event listener
    window.addEventListener('resize', this.handleResize);

    // Load rack configurations and box splitting rules
    await this.loadRackConfigs();
    await this.loadSplitRules();

    // If we have query parameters, load the distribution automatically
    if (this.selectedDates && this.selectedDates.length > 0) {
      this.loadDistribution();
    }
  },

  beforeDestroy() {
    // Remove resize event listener when component is destroyed
    window.removeEventListener('resize', this.handleResize);
  },
  computed: {
    // Raw histogram by meal-space, honouring the include Home / Office toggles.
    // home = distribution; office (and other non-home) = distributionAll - distribution.
    rawDistribution() {
      const home = this.distributionData.distribution || {};
      const all = this.distributionData.distributionAll || {};
      const spaces = new Set([...Object.keys(home), ...Object.keys(all)]);
      return [...spaces]
        .map(space => {
          const h = home[space] || 0;
          const office = Math.max(0, (all[space] || 0) - h);
          const count = (this.includeHome ? h : 0) + (this.includeOffice ? office : 0);
          return {space: parseFloat(space), count};
        })
        .filter(item => item.count > 0)
        .sort((a, b) => a.space - b.space);
    },
    // By Rack bars: per box size, an unconstrained "size total" entry followed by
    // that size's individual rack entries
    rackBars() {
      if (!this.groupedData.length) return [];
      const meta = {};
      this.boxSizeData.forEach(b => meta[b.size] = b);
      const bySize = {};
      this.groupedData.forEach(g => (bySize[g.boxSize] = bySize[g.boxSize] || []).push(g));
      const out = [];
      Object.keys(bySize).map(Number).sort((a, b) => a - b).forEach(size => {
        const m = meta[size] || {};
        const demand = m.demand || 0;
        const cap = m.capacityAll || 0; // unconstrained total capacity
        out.push({
          key: 'total-' + size,
          isTotal: true,
          boxType: `All ${size}-meal boxes`,
          start: m.min,
          end: size,
          boxSize: size,
          totalDemand: demand,
          usableCapacity: cap,
          coveredDemand: Math.min(demand, cap),
          spareCap: Math.max(0, cap - demand),
          overCap: Math.max(0, demand - cap),
          overLimit: Math.max(0, demand - cap),
          barTotal: Math.max(demand, cap),
          rackCount: m.rackCount || 0,
          splitInCount: 0,
          overflowKind: null,
          chips: []
        });
        bySize[size].forEach((g, i) => out.push({...g, key: `rack-${size}-${i}`, groupStart: i === 0}));
      });
      return out;
    },
    dateFrom() {
      return this.selectedDates && this.selectedDates.length
        ? [...this.selectedDates].sort()[0]
        : null;
    },
    dateTo() {
      return this.selectedDates && this.selectedDates.length
        ? [...this.selectedDates].sort().reverse()[0]
        : null;
    },
    shippingGroups() {
      return this.selectedShippingGroup ? [this.selectedShippingGroup] : [];
    },
    hasDistributionData() {
      return Object.keys(this.distributionData.distribution).length > 0 ||
        Object.keys(this.distributionData.distributionAll).length > 0;
    },
    maxRawValue() {
      return this.rawDistribution.length
        ? Math.max(...this.rawDistribution.map(item => item.count), 1)
        : 1;
    },
    maxGroupedValue() {
      // bar height = max(demand, capacity), so the axis must span both
      const groupedMax = this.groupedData.length
        ? Math.max(...this.groupedData.map(g => Math.max(g.totalDemand || 0, g.usableCapacity || 0)))
        : 0;
      // size-total bars use the unconstrained capacity of all racks of that size
      const sizeMax = this.boxSizeData.length
        ? Math.max(...this.boxSizeData.map(b => Math.max(b.demand || 0, b.capacityAll || 0)))
        : 0;
      const ungroupedMax = this.ungroupedData.count || 0;
      return Math.max(groupedMax, sizeMax, ungroupedMax, 1);
    },
    maxBoxSizeValue() {
      return this.boxSizeData.length
        ? Math.max(...this.boxSizeData.map(b => Math.max(b.demand || 0, b.capacity || 0)), 1)
        : 1;
    },
    totalRaw() {
      return this.rawDistribution.reduce((sum, item) => sum + item.count, 0);
    },
    totalGroupedCount() {
      const groupedTotal = this.groupedData.reduce((sum, group) => sum + group.demand, 0);
      return groupedTotal + this.ungroupedData.count;
    },
    totalBoxCapacity() {
      return this.groupedData.reduce((sum, group) => sum + group.usableCapacity, 0);
    },
    totalBoxSizeDemand() {
      return this.boxSizeData.reduce((sum, b) => sum + b.demand, 0);
    },
    hasSplitActivity() {
      return this.groupedData.some(g => g.splitInCount > 0 || g.overflowKind === 'split');
    },
    barContainerClass() {
      const itemCount = this.activeTab === 0
        ? this.rackBars.length
        : this.activeTab === 1
          ? this.boxSizeData.length
          : this.rawDistribution.length;

      // Determine if we need to override min-width based on the number of items
      if (itemCount > 15) {
        return 'bar-container small-bars';
      }

      return 'bar-container';
    }
  },
  methods: {
    async loadRackConfigs() {
      try {
        const response = await api.get('v2/order/rack-configs');
        this.rackConfigs = response.data;
        this.rackConfigs.sort((a, b) => a.mealsPerBinMinimum - b.mealsPerBinMinimum);
      } catch (error) {
        console.error('Error loading rack configurations:', error);
        this.$store.dispatch('setSnackbar', {
          text: `Error loading rack configurations: ${error.response?.data?.error || error.message}`,
          color: 'error'
        });
      }
    },
    
    async loadSplitRules() {
      try {
        const response = await api.get('v2/order/box-split-rules');
        this.splitRules = response.data;
      } catch (error) {
        console.error('Error loading box splitting rules:', error);
      }
    },

    getBoxCount(config) {
      return config.maxRacks * config.rowsOfBins * config.binsPerRow;
    },

    isOfficeOnly(config) {
      const locations = config.locations || [];
      return locations.length > 0 && locations.every(l => l === 'office');
    },

    isHomeOnly(config) {
      const locations = config.locations || [];
      return locations.length > 0 && locations.every(l => l === 'home');
    },

    // small labels describing a rack's restrictions ("rules"), eg office-only,
    // shipping-group reservation, split target, pickup
    reservationChips(config, {away, isTarget}) {
      const chips = [];
      if (this.isOfficeOnly(config)) {
        chips.push({text: 'office only', color: 'deep-purple'});
      } else if (this.isHomeOnly(config)) {
        chips.push({text: 'home only', color: 'blue-grey'});
      } else if ((config.locations || []).length > 0) {
        chips.push({text: config.locations.join('/'), color: 'blue-grey'});
      }
      if (config.shippingGroup) {
        chips.push(away
          ? {text: `reserved for ${config.shippingGroup}`, color: 'deep-orange'}
          : {text: `${config.shippingGroup} only`, color: 'teal'});
      }
      if (config.splitOnly) {
        chips.push({text: 'split only', color: 'purple'});
      } else if (isTarget) {
        chips.push({text: 'overflow target', color: 'purple'});
      }
      if (config.includePickupOrders) {
        chips.push({text: 'pickup ok', color: 'green'});
      }
      return chips;
    },

    handleResize() {
      this.windowWidth = window.innerWidth;
      this.$forceUpdate(); // Force re-render to adjust bar widths
    },
    handleProductionUpdate({dates, shipping_group}) {
      // Clear current distribution data if dates or shippingGroup changes
      if (this.selectedDates !== dates || this.selectedShippingGroup !== shipping_group) {
        this.distributionData = {
          distribution: {},
          distributionAll: {}
        };
        this.groupedData = [];
      }
      this.selectedDates = dates;
      this.selectedShippingGroup = shipping_group;
    },
    async loadDistribution() {
      if (this.dateFrom && this.dateTo && this.shippingGroups && !this.loading) {
        this.loading = true;
        try {
          const response = await api.post('/v2/order/bin-distribution', {
            dateFrom: this.dateFrom,
            dateTo: this.dateTo,
            isAdmin: true, // Always use admin mode since we removed the switch
            shippingGroups: this.shippingGroups
          });

          // Save the distribution data for charts
          this.distributionData = response.data;

          // Initialize the grouped data
          this.updateGroupData();
        } catch (error) {
          console.error('Error loading box distribution:', error);
          this.$store.dispatch('setSnackbar', {
            text: `Error loading box distribution: ${error.response?.data?.error || error.message}`,
            color: 'error'
          });
        } finally {
          this.loading = false;
        }
      }
    },
    // sum the order counts in a data map whose keys are meal-space values within
    // [min, max]
    rangeSum(data, min, max) {
      return Object.keys(data)
        .map(parseFloat)
        .filter(s => s >= min && s <= max)
        .reduce((sum, s) => sum + (data[s.toString()] || 0), 0);
    },

    // Builds the "by rack configuration" view: one entry per rack config (all of
    // them, including office and shipping-group-reserved ones), each showing its
    // demand, capacity, restriction chips and any split overflow.
    updateGroupData() {
      if (!this.hasDistributionData || this.rackConfigs.length === 0) {
        this.groupedData = [];
        this.boxSizeData = [];
        this.ungroupedData = {count: 0, spaces: [], spacesFormatted: '', rangeLabel: ''};
        return;
      }

      const homeData = this.distributionData.distribution || {};   // home orders
      const allData = this.distributionData.distributionAll || {}; // home + office
      // home racks serve the home portion of a size range; office-only racks
      // serve the office portion (all orders minus the home orders)
      const scopeDemand = (config) => {
        const {mealsPerBinMinimum: min, mealsPerBin: max} = config;
        return this.isOfficeOnly(config)
          ? Math.max(0, this.rangeSum(allData, min, max) - this.rangeSum(homeData, min, max))
          : this.rangeSum(homeData, min, max);
      };

      const selected = this.selectedShippingGroup;
      const reservedAway = c => !!(selected && c.shippingGroup && c.shippingGroup !== selected);
      const splitTargetTypes = new Set(this.splitRules.flatMap(r => [r.targetBoxType1, r.targetBoxType2]));

      // Attribute each (size range + location scope)'s demand to a single primary
      // rack, so that a dedicated overflow rack (eg Temp Blue) sharing a size with
      // a primary rack (Blue) does not double-count the same orders.
      const buckets = {};
      this.rackConfigs.forEach(c => {
        const key = `${c.mealsPerBinMinimum}-${c.mealsPerBin}|${this.isOfficeOnly(c) ? 'office' : 'home'}`;
        (buckets[key] = buckets[key] || []).push(c);
      });
      const baseDemandByBoxType = {};
      Object.values(buckets).forEach(list => {
        const primary = list.find(c => !splitTargetTypes.has(c.boxType) && !c.splitOnly) || list[0];
        baseDemandByBoxType[primary.boxType] = scopeDemand(primary);
      });

      const groups = this.rackConfigs.map(c => {
        const away = reservedAway(c);
        const capacity = this.getBoxCount(c);
        const isTarget = splitTargetTypes.has(c.boxType) || !!c.splitOnly;
        return {
          boxType: c.boxType,
          start: c.mealsPerBinMinimum,
          end: c.mealsPerBin,
          boxSize: c.mealsPerBin,
          capacity,
          usableCapacity: away ? 0 : capacity,
          rackCount: c.maxRacks,
          demand: baseDemandByBoxType[c.boxType] || 0,
          splitInCount: 0,
          shippingGroup: c.shippingGroup || null,
          isOffice: this.isOfficeOnly(c),
          isSplitTarget: isTarget,
          reservedAway: away,
          splitTargets: null,
          splitOutCount: 0,
          chips: this.reservationChips(c, {away, isTarget})
        };
      });
      const byBoxType = {};
      groups.forEach(g => byBoxType[g.boxType] = g);

      // A source that is reserved away (unusable for the selected group) or over
      // capacity routes its overflow into the two target boxes. Skipped when the
      // "show split demand" toggle is off, so bars show demand before splitting.
      if (this.showSplit) {
        this.splitRules.forEach(rule => {
          const src = byBoxType[rule.sourceBoxType];
          if (!src) return;
          const overflow = Math.max(0, src.demand - src.usableCapacity);
          if (overflow === 0) return;
          const targets = [rule.targetBoxType1, rule.targetBoxType2].map(t => byBoxType[t]).filter(Boolean);
          if (targets.length === 0) return;
          targets.forEach(t => t.splitInCount += overflow);
          src.splitTargets = [rule.targetBoxType1, rule.targetBoxType2];
          src.splitOutCount = overflow;
        });
      }

      groups.forEach(g => {
        g.totalDemand = g.demand + g.splitInCount;
        g.overLimit = Math.max(0, g.totalDemand - g.usableCapacity);
        // stacked bar: blue = covered demand, green stripes = spare capacity,
        // red = demand over capacity. Bar height = max(demand, capacity).
        g.coveredDemand = Math.min(g.totalDemand, g.usableCapacity);
        g.spareCap = Math.max(0, g.usableCapacity - g.totalDemand);
        g.overCap = g.overLimit;
        g.barTotal = Math.max(g.totalDemand, g.usableCapacity);
        g.overflowKind = g.splitTargets ? 'split' : (g.overLimit > 0 ? 'regular' : null);
      });

      // order by box size, home before office, then name
      groups.sort((a, b) =>
        a.boxSize - b.boxSize ||
        (a.isOffice === b.isOffice ? a.boxType.localeCompare(b.boxType) : (a.isOffice ? 1 : -1)));
      this.groupedData = groups;

      // orders whose meal-space matches no configured box range
      const ranges = this.rackConfigs.map(c => [c.mealsPerBinMinimum, c.mealsPerBin]);
      const inAnyRange = s => ranges.some(([mn, mx]) => s >= mn && s <= mx);
      const ungroupedSpaces = Object.keys(allData)
        .map(parseFloat)
        .filter(s => !inAnyRange(s))
        .sort((a, b) => a - b);
      if (ungroupedSpaces.length > 0) {
        const count = ungroupedSpaces.reduce((sum, s) => sum + (allData[s.toString()] || 0), 0);
        const min = Math.min(...ungroupedSpaces);
        const max = Math.max(...ungroupedSpaces);
        this.ungroupedData = {
          count,
          spaces: ungroupedSpaces,
          spacesFormatted: ungroupedSpaces.join(', '),
          rangeLabel: min === max ? `${min}` : `${min}-${max}`
        };
      } else {
        this.ungroupedData = {count: 0, spaces: [], spacesFormatted: '', rangeLabel: ''};
      }

      this.buildBoxSizeData(homeData, allData, reservedAway);
    },

    // Builds the "by box size" view: buckets demand by the box size an order
    // needs (the max of each rack's range, eg 2, 4, 6, 8, 10, 12), independent
    // of which specific rack or location serves it. Demand is split into home
    // and office portions so the bars can be stacked.
    buildBoxSizeData(homeData, allData, reservedAway) {
      const bySize = {};
      this.rackConfigs.forEach(c => {
        const size = c.mealsPerBin;
        const b = bySize[size] = bySize[size] || {
          size, min: c.mealsPerBinMinimum, capacity: 0, capacityAll: 0, rackCount: 0, boxTypes: []
        };
        b.min = Math.min(b.min, c.mealsPerBinMinimum);
        // capacityAll = unconstrained total (incl reserved); capacity = usable now
        b.capacityAll += this.getBoxCount(c);
        if (!reservedAway(c)) {
          b.capacity += this.getBoxCount(c);
          b.rackCount += c.maxRacks;
        }
        b.boxTypes.push(c.boxType);
      });
      const sizes = Object.values(bySize).sort((a, b) => a.size - b.size);
      sizes.forEach(b => {
        b.home = this.rangeSum(homeData, b.min, b.size);
        b.demand = this.rangeSum(allData, b.min, b.size);
        b.office = Math.max(0, b.demand - b.home);
        b.overLimit = Math.max(0, b.demand - b.capacity);
      });
      this.boxSizeData = sizes;
    },

    // segment height as a percentage of its stacked bar
    segmentPct(part, total) {
      return total > 0 ? (part / total) * 100 : 0;
    }
  },
  watch: {
    // Update grouped data whenever distribution data changes
    'distributionData': {
      handler() {
        this.updateGroupData();
      },
      deep: true
    },
    // Update grouped data when rack configs are loaded
    'rackConfigs': {
      handler() {
        this.updateGroupData();
      },
      deep: true
    },
    // Update grouped data when split rules are loaded
    'splitRules': {
      handler() {
        this.updateGroupData();
      },
      deep: true
    },
    // recompute By Rack demand when the split toggle changes
    'showSplit': function () {
      this.updateGroupData();
    },
    // Clear data when selected dates change from a URL update
    'selectedDates': function () {
      this.distributionData = {
        distribution: {},
        distributionAll: {}
      };
      this.groupedData = [];
      this.loadDistribution();
    },
    // Clear data when selected production changes from a URL update
    'selectedShippingGroup': function () {
      this.distributionData = {
        distribution: {},
        distributionAll: {}
      };
      this.groupedData = [];
      this.loadDistribution();
    }
  }
};
</script>

<style scoped>
.chart-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 10px 0 20px 10px;
}

.chart-container {
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding: 20px 20px 0 40px;
  position: relative;
}

/* bars scroll horizontally so each column keeps a readable fixed width;
   the y-axis stays put (absolutely positioned in .chart-container) */
.bars-container {
  display: flex;
  align-items: flex-start;
  width: 100%;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 6px;
}

/* fixed-height plot so every bar's baseline sits on the same zero line;
   the text label sits below the plot */
.col-plot {
  height: 300px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  position: relative;
}

.col-label {
  width: 100%;
  text-align: center;
  padding-top: 6px;
}

.x-axis-label {
  text-align: center;
  font-weight: bold;
  margin-top: 20px;
  color: rgba(0, 0, 0, 0.7);
}

.y-axis-label {
  position: absolute;
  transform: rotate(-90deg);
  transform-origin: left top;
  font-weight: bold;
  left: 0;
  top: 50%;
  color: rgba(0, 0, 0, 0.7);
}

.y-axis {
  position: absolute;
  left: 0;
  top: 20px;
  height: 300px;
  width: 40px;
}

.y-tick {
  position: absolute;
  right: 0;
  width: 40px;
  text-align: right;
  font-size: 12px;
  padding-right: 5px;
  border-top: 1px dashed rgba(0, 0, 0, 0.1);
}

.bar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 0 0 auto;
  width: 78px;
}

/* wrapper spans the column so the bar's width:100% resolves even when the bar
   has no text inside it */
.bar-container-wrapper {
  width: 100%;
}

.bar {
  width: 100%;
  max-width: 40px;
  min-height: 20px;
  margin: 0 auto;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  position: relative;
  transition: height 0.5s ease;
}

.bar.primary {
  background-color: #1976D2;
}

.bar.secondary {
  background-color: #00BCD4;
}

.bar.accent {
  background-color: #FF5722;
}

.bar.warning {
  background-color: #FFA726;
}

.bar-value {
  color: white;
  font-weight: bold;
  padding: 0 0 4px 0;
  text-shadow:
    -1px -1px 0 #000,
     1px -1px 0 #000,
    -1px  1px 0 #000,
     1px  1px 0 #000,
     0   -1px 0 #000,
     0    1px 0 #000,
    -1px  0   0 #000,
     1px  0   0 #000;
}

.bar-label {
  margin-top: 5px;
  font-weight: bold;
}

.detailed-bar-label {
  margin-top: 5px;
  text-align: center;
  width: 100%;
}

.box-type-label {
  font-size: 11px;
  font-weight: bold;
  color: #1976D2;
  margin-bottom: 2px;
}

.box-type-label.ungrouped {
  color: #F57C00;
}

.ungrouped-row {
  background-color: #FFF3E0;
}

.size-total-row {
  background-color: #ECEFF1;
  font-weight: bold;
}

.range-label {
  font-weight: bold;
}

.values-label {
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  transition: all 0.3s ease;
}

.small-bars {
  width: 66px;
}

.box-count-line-wrapper {
  position: absolute;
  left: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 5;
}

.box-count-line {
  width: 100%;
  height: 2px;
  background-color: #4CAF50;
  border: 1px dashed red;
}

.box-count-label {
  font-size: 10px;
  font-weight: bold;
  color: red;
  margin-top: 2px;
}

.box-count-label-top {
  font-size: 10px;
  font-weight: bold;
  color: red;
  margin-bottom: 2px;
  text-align: center;
  white-space: nowrap;
  background-color: rgba(255, 255, 255, 0.9);
  padding: 1px 4px;
  border-radius: 2px;
}

.over-limit-label-top {
  font-size: 9px;
  font-weight: bold;
  color: purple;
  padding: 2px 4px;
  text-align: center;
  width: 100%;
  margin-bottom: 2px;
}

.over-limit-label-top.split-out {
  color: #9C27B0;
}

.split-in-label {
  font-size: 9px;
  font-weight: bold;
  color: #9C27B0;
  text-align: center;
  width: 100%;
  margin-top: 2px;
}

.bar.reserved-bar {
  background-color: #B0BEC5;
  background-image: repeating-linear-gradient(
    45deg, transparent, transparent 4px,
    rgba(156, 39, 176, 0.4) 4px, rgba(156, 39, 176, 0.4) 8px
  );
}

.size-group-start {
  border-left: 2px solid #B0BEC5;
  padding-left: 4px;
}

.total-label {
  font-weight: bold;
  color: #37474F;
}

/* By Rack: leave headroom above the bars so the capacity/destination labels
   are not clipped (bars use the bottom 240px of the 300px plot) */
.rack-chart .y-axis {
  top: 80px;
  height: 240px;
}

/* stacked demand bar: green within capacity, red over capacity */
.rack-stack {
  width: 100%;
  max-width: 40px;
  min-height: 3px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  transition: height 0.5s ease;
}

.rack-stack.is-total {
  outline: 2px solid #37474F;
  outline-offset: -1px;
}

.rack-seg {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  min-height: 0;
}

.rack-seg.covered-seg { background-color: #1976D2; }
.rack-seg.over-seg    { background-color: #E53935; }
.rack-seg.noboxsize-seg { background-color: #FFA726; }
/* spare capacity: green stripes */
.rack-seg.spare-seg {
  background-color: #66BB6A;
  background-image: repeating-linear-gradient(
    45deg, transparent, transparent 4px,
    rgba(255, 255, 255, 0.55) 4px, rgba(255, 255, 255, 0.55) 8px
  );
}

.seg-num {
  font-size: 9px;
  font-weight: bold;
  color: #fff;
  text-shadow:
    -1px -1px 0 rgba(0,0,0,.5), 1px -1px 0 rgba(0,0,0,.5),
    -1px  1px 0 rgba(0,0,0,.5), 1px  1px 0 rgba(0,0,0,.5);
}

.seg-num.spare-num {
  color: #1B5E20;
  text-shadow: none;
}

.cap-top {
  font-size: 10px;
  font-weight: bold;
  color: #37474F;
  white-space: nowrap;
  text-align: center;
  margin-bottom: 1px;
}

.over-dest {
  font-size: 9px;
  font-weight: bold;
  color: #C62828;
  text-align: center;
  line-height: 1.1;
  margin-bottom: 2px;
}

.over-dest.split-out {
  color: #9C27B0;
}

/* demand value shown below the baseline for the By Rack chart */
.demand-label {
  font-size: 12px;
  font-weight: bold;
  color: #37474F;
  margin-bottom: 1px;
}

.split-in-below {
  font-size: 9px;
  font-weight: bold;
  color: #9C27B0;
  margin-top: 2px;
}

/* fixed row heights so each label line aligns horizontally across every bar
   (demand on one row, box type on the next, etc.) */
.rack-col-label {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.rack-col-label .demand-label {
  height: 16px;
  line-height: 16px;
  margin-bottom: 2px;
  font-size: 12px;
  white-space: nowrap;
}

.rack-col-label .box-type-label {
  height: 32px;
  margin-bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1.05;
  font-size: 10px;
  overflow: hidden;
  word-break: break-word;
}

.rack-col-label .range-label {
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1.05;
  font-size: 10px;
  font-weight: normal;
  color: rgba(0, 0, 0, 0.7);
}

.rack-col-label .chip-row {
  min-height: 34px;
  align-content: flex-start;
  margin-top: 2px;
}

.rack-col-label .split-in-below {
  height: 12px;
}

.reserved-note {
  font-size: 9px;
  font-weight: normal;
  color: #9C27B0;
}

.split-in-note {
  color: #9C27B0;
  font-size: 11px;
}

.chip-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2px;
  margin-top: 2px;
}

.mini-chip {
  display: inline-block;
  font-size: 9px;
  line-height: 1.2;
  padding: 1px 5px;
  margin: 1px;
  border-radius: 8px;
  color: #fff;
  white-space: nowrap;
}

.mini-chip-deep-purple { background-color: #673AB7; }
.mini-chip-blue-grey  { background-color: #607D8B; }
.mini-chip-deep-orange { background-color: #E64A19; }
.mini-chip-teal       { background-color: #00897B; }
.mini-chip-purple     { background-color: #9C27B0; }
.mini-chip-green      { background-color: #43A047; }

.reserved-swatch {
  background-color: #B0BEC5;
  background-image: repeating-linear-gradient(
    45deg, transparent, transparent 3px,
    rgba(156, 39, 176, 0.5) 3px, rgba(156, 39, 176, 0.5) 6px
  );
}

.spare-swatch {
  background-color: #66BB6A;
  background-image: repeating-linear-gradient(
    45deg, transparent, transparent 3px,
    rgba(255, 255, 255, 0.6) 3px, rgba(255, 255, 255, 0.6) 6px
  );
}

/* stacked box-size bar: home at the bottom, office on top */
.bar-stack {
  width: 100%;
  max-width: 46px;
  min-height: 4px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  transition: height 0.5s ease;
}

.bar-seg {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  min-height: 0;
}

.bar-seg.home-seg {
  background-color: #1976D2;
}

.bar-seg.office-seg {
  background-color: #90CAF9;
}

.seg-label {
  font-size: 9px;
  font-weight: bold;
  color: #fff;
  white-space: nowrap;
  text-shadow:
    -1px -1px 0 rgba(0,0,0,.6), 1px -1px 0 rgba(0,0,0,.6),
    -1px  1px 0 rgba(0,0,0,.6), 1px  1px 0 rgba(0,0,0,.6);
}

.office-seg .seg-label {
  color: #0d47a1;
  text-shadow: none;
}

.bar-total-label {
  font-size: 10px;
  font-weight: bold;
  color: #1976D2;
  text-align: center;
  white-space: nowrap;
  margin-bottom: 2px;
}

.bar-total-label .over-cap {
  color: #C62828;
}

.chart-legend {
  display: flex;
  justify-content: center;
  margin-top: 10px;
  gap: 20px;
}

.legend-item {
  display: flex;
  align-items: center;
  font-size: 12px;
}

.legend-color-box {
  width: 20px;
  height: 10px;
  margin-right: 5px;
}

@media (max-width: 960px) {
  .bar-container {
    min-width: 30px;
  }

  .bar-value {
    font-size: 12px;
  }

  .bar-label, .range-label {
    font-size: 12px;
  }

  .values-label {
    font-size: 10px;
  }

  .small-bars {
    min-width: 25px;
  }

  .y-axis-label, .x-axis-label {
    font-size: 14px;
  }

  .y-tick {
    font-size: 10px;
  }
}

@media (max-width: 600px) {
  .bar-container {
    min-width: 25px;
  }

  .small-bars {
    min-width: 20px;
  }

  .bar-value {
    font-size: 10px;
  }

  .chart-container:hover .bar-container,
  .chart-container:hover .small-bars {
    min-width: 30px;
  }

  .y-axis-label, .x-axis-label {
    font-size: 12px;
  }

  .y-tick {
    font-size: 9px;
    width: 30px;
  }

  .chart-container {
    padding-left: 30px;
  }

  .y-axis {
    width: 30px;
  }
}
</style>